import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useController, ApplicationView } from '@aha-app/mvc';
import Tool, { type ToolButtonComponent } from './Tool';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';
import trackEvent from '../utils/analytics';
import WithHint from '../toolbars/WithHint';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import { useTippyTarget } from '../components/tippyContext';

const Popup = ({ controller, selection }) => {
  const { startDate, endDate } = selection.firstObject();
  const [objStartDate, setObjStartDate] = useState(startDate);
  const [objEndDate, setObjEndDate] = useState(endDate);
  const startDateCalendarRef = useRef<HTMLAhaCalendarElement>();
  const endDateCalendarRef = useRef<HTMLAhaCalendarElement>();

  useEffect(() => {
    if (!startDateCalendarRef.current) return;
    if (!endDateCalendarRef.current) return;
    const chooseDate = (field, date) => {
      const newDate = moment(date).unix();
      let newStart, newEnd;
      if (field === 'startDate') {
        newStart = newDate;
        newEnd = objEndDate;
      } else {
        newStart = objStartDate;
        newEnd = newDate;
      }

      if (newEnd <= newStart) {
        controller.actionShowMessage(
          'The end date cannot be before the start date'
        );
        return;
      } else {
        controller.actionShowMessage(null);
      }

      selection.setOnObjects(field, newDate);
      if (field === 'startDate') {
        setObjStartDate(newDate);
      } else {
        setObjEndDate(newDate);
      }
      controller.canvas?.requestRenderAll();
      trackEvent(
        'Feature-7177137609894815136',
        'User edits timeline duration',
        {
          duration: 'Custom via calendar',
        }
      );
    };

    startDateCalendarRef.current.addEventListener(
      'aha-calendar:selected',
      event => chooseDate('startDate', event.detail.element.date)
    );
    endDateCalendarRef.current.addEventListener(
      'aha-calendar:selected',
      event => chooseDate('endDate', event.detail.element.date)
    );
  }, [controller, objEndDate, objStartDate, selection]);

  return (
    <div
      style={{
        width: 560,
        height: 290,
        paddingLeft: 30,
        paddingRight: 30,
        paddingTop: 40,
        paddingBottom: 5,
        display: 'flex',
      }}
    >
      <div style={{ paddingRight: 40 }}>
        <aha-calendar
          date={moment.unix(objStartDate).format('YYYY-MM-DD')}
          controls
          ref={startDateCalendarRef}
        />
      </div>
      <div style={{ paddingLeft: 40, borderLeft: '1px solid #F1F1F1' }}>
        <aha-calendar
          date={moment.unix(objEndDate).format('YYYY-MM-DD')}
          controls
          ref={endDateCalendarRef}
        />
      </div>
    </div>
  );
};

const DateRangeButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    const controller = useController(WhiteboardController);
    const tippyRef = useRef<TippyInstance>();
    const toolbarSingleton = useTippyTarget();

    const popup = <Popup selection={selection} controller={controller} />;

    const [shouldRenderPopup, setShouldRenderPopup] = useState(false);

    const handlePredefinedRangeSelection = months => {
      selection.setOnObjects('startDate', moment().startOf('month').unix());
      selection.setOnObjects(
        'endDate',
        moment().startOf('month').add(months, 'months').unix()
      );
      selection.canvas.requestRenderAll();

      let time = months;
      let unit = 'M';
      if (months === 12) {
        time = 1;
        unit = 'Y';
      }

      trackEvent(
        'Feature-7177137609894815136',
        'User edits timeline duration',
        {
          duration: `${time}${unit}`,
        }
      );
    };

    const { startDate, endDate } = selection.firstObject();
    const startsAtFirstOfMonth = startDate === moment().startOf('month').unix();
    const diff = moment
      .unix(endDate)
      .diff(moment.unix(startDate), 'months', true);

    let checked = 'custom';
    if (startsAtFirstOfMonth && diff === 3) {
      checked = '3M';
    }

    if (startsAtFirstOfMonth && diff === 6) {
      checked = '6M';
    }

    if (startsAtFirstOfMonth && diff === 12) {
      checked = '1Y';
    }

    const button = (
      <aha-button-group style={{ marginRight: 5, marginLeft: 5 }}>
        <aha-radio-button-group
          first-render
          style={{ '--aha-radio-button--selected-transition-time': '0s' }}
        >
          <aha-radio-button
            role='radio'
            aria-checked={checked === '3M' ? 'true' : 'false'}
            selected={checked === '3M' ? true : null}
            onClick={() => handlePredefinedRangeSelection(3)}
          >
            3M
          </aha-radio-button>
          <aha-radio-button
            role='radio'
            aria-checked={checked === '6M' ? 'true' : 'false'}
            selected={checked === '6M' ? true : null}
            onClick={() => handlePredefinedRangeSelection(6)}
          >
            6M
          </aha-radio-button>
          <aha-radio-button
            role='radio'
            aria-checked={checked === '1Y' ? 'true' : 'false'}
            selected={checked === '1Y' ? true : null}
            onClick={() => handlePredefinedRangeSelection(12)}
          >
            1Y
          </aha-radio-button>
          <aha-radio-button
            role='radio'
            aria-checked={checked === 'custom' ? 'true' : 'false'}
            selected={checked === 'custom' ? true : null}
          >
            <Tippy
              arrow={false}
              content={shouldRenderPopup ? popup : null}
              onShow={instance => {
                // When showing the tippy, get a reference to the instance so that
                // it can be controlled in a more fine-grained way - especially to
                // call `hide()` on demand to hide it.
                if (tippyRef) tippyRef.current = instance;
                setShouldRenderPopup(true);
              }}
              onHide={() => setShouldRenderPopup(false)}
              theme='light'
              trigger='click'
              interactive
              duration={75}
              animation='scale'
              maxWidth={1500}
              offset={[0, 25]}
            >
              <span>Custom</span>
            </Tippy>
          </aha-radio-button>
        </aha-radio-button-group>
      </aha-button-group>
    );

    const withHint =
      toolbarSingleton && tool ? (
        <WithHint tool={tool} singleton={toolbarSingleton}>
          {button}
        </WithHint>
      ) : (
        button
      );

    return withHint;
  }
);

class ToolDateRange extends Tool {
  static toolName = 'Date range';

  static buttonComponent() {
    return DateRangeButton;
  }
}

export default ToolDateRange;
